import client from './client'
import type { ApiResponse, AuthResponse, User, LoginCredentials } from '@/types'

export interface CheckUserResult {
  exists: boolean
  has_password?: boolean
  role?: string
  /** Ops phones only: the support PIN has never been set (or was cleared),
   *  and only the admin portal can set it. */
  needs_portal_migration?: boolean
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await client.post<ApiResponse<AuthResponse>>('/auth/login', credentials)
    return response.data.data!
  },

  refresh: async (refreshToken: string): Promise<{ access_token: string; refresh_token: string }> => {
    const response = await client.post<ApiResponse<{ access_token: string; refresh_token: string }>>(
      '/auth/refresh',
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    )
    return response.data.data!
  },

  getMe: async (): Promise<User> => {
    const response = await client.get<ApiResponse<User>>('/auth/me')
    return response.data.data!
  },

  updateMe: async (data: Partial<User>): Promise<User> => {
    const response = await client.put<ApiResponse<User>>('/auth/me', data)
    return response.data.data!
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await client.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
  },

  // Web PIN+OTP authentication

  // `phone` may carry the `-ops` marker (`+254712345678-ops`) for a platform
  // support login — the browser is on the sacco's own domain, so the phone
  // field is the only thing that can signal it.
  checkUser: async (phone: string): Promise<CheckUserResult> => {
    const response = await client.post<ApiResponse<CheckUserResult>>(
      '/auth/check',
      { phone }
    )
    return response.data.data!
  },

  loginWithPin: async (phone: string, pin: string): Promise<{ otp_sent: boolean; expires_in: number; phone_masked: string }> => {
    const response = await client.post<ApiResponse<{ otp_sent: boolean; expires_in: number; phone_masked: string }>>(
      '/auth/login/pin',
      { phone, pin }
    )
    return response.data.data!
  },

  verifyLoginOtp: async (phone: string, code: string): Promise<AuthResponse> => {
    const response = await client.post<ApiResponse<AuthResponse>>(
      '/auth/login/verify-otp',
      { phone, code }
    )
    return response.data.data!
  },

  setupPin: async (phone: string, pin: string, confirmPin: string): Promise<void> => {
    await client.post('/auth/setup-pin', {
      phone,
      pin,
      confirm_pin: confirmPin,
    })
  },

  forgotPin: async (phone: string): Promise<void> => {
    await client.post('/auth/forgot-pin', { phone })
  },

  forgotPinVerify: async (phone: string, code: string): Promise<{ reset_token: string }> => {
    const response = await client.post<ApiResponse<{ reset_token: string }>>(
      '/auth/forgot-pin/verify',
      { phone, code }
    )
    return response.data.data!
  },

  /** End the caller's own support session (ops sessions only). */
  endSupportSession: async (): Promise<void> => {
    await client.post('/auth/support-session/end')
  },

  forgotPinSetPin: async (phone: string, resetToken: string, pin: string, confirmPin: string): Promise<void> => {
    await client.post('/auth/forgot-pin/set-pin', {
      phone,
      reset_token: resetToken,
      pin,
      confirm_pin: confirmPin,
    })
  },
}
